import {
  Col,
  ConfigProvider,
  Empty,
  ModalProps,
  Row,
  Select,
  Spin,
  Typography,
} from "antd";
import {
  InventoryVm,
  WorkbookTaskItem,
  WorkbookTaskVm,
} from "api/generated/lumen";
import { useDispatch, useSelector } from "app/store";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getDictionaryItemLabel } from "utils";
import styles from "./AddTaskItemModal.module.scss";
import clsx from "clsx";
import ItemChooserModal from "../ItemChooserModal";
import ItemChooserSearch from "../ItemChooserSearch";
import ItemChooserTask from "../ItemChooserTask";
import SetItemCount from "./SetItemCount";
import ImageDisplay from "components/ui/ImageDisplay";
import { DeviceLayoutType } from "models/common";
import useInventoryCategoriesQuery from "hooks/queries/useInventoryCategoriesQuery";
import { MaterialIcon } from "components/MaterialIcon";
import { mdiMagnify } from "@mdi/js";

interface AddTaskItemModalProps extends ModalProps {
  task: WorkbookTaskVm;
  workbookTaskItemToEdit: WorkbookTaskItem | undefined;
  deviceLayoutType?: DeviceLayoutType;
}

type ContentMode = "chooseItem" | "setCount";

const AddTaskItemModal: React.FC<AddTaskItemModalProps> = ({
  onCancel,
  visible,
  task,
  workbookTaskItemToEdit,
  deviceLayoutType,
  ...rest
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { fetchInventoryItems, addTaskItemToWorkbookTask, editTaskItemCount } =
    useSelector((state) => state.loading.effects.failureView);

  const {
    failure,
    inventoryItems,
    inventoryItemTypes,
    inventoryItemManufactures,
  } = useSelector((state) => state.failureView);

  const inventoryCategoriesQuery = useInventoryCategoriesQuery(true);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [contentMode, setContentMode] = useState<ContentMode>("chooseItem");
  const [chosenItem, setChosenItem] = useState<InventoryVm>();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>();
  const [selectedManufacturer, setSelectedManufacturer] = useState<
    string | null
  >();
  const [selectedInventoryType, setSelectedInventoryType] = useState<
    string | null
  >();

  const handleSelectedCategoryId = (id: string) => {
    setSelectedCategoryId(id);
  };

  const handleSelectedManufacturer = (id: string) => {
    setSelectedManufacturer(id);
  };

  const handleSelectedInventoryType = (id: string) => {
    setSelectedInventoryType(id);
  };

  const resetAllSelect = () => {
    setSelectedCategoryId(null);
    setSelectedManufacturer(null);
    setSelectedInventoryType(null);
  };

  const sortedInventoryCategories = useMemo(
    () =>
      [...(inventoryCategoriesQuery.data?.items || [])].sort((a, b) =>
        getDictionaryItemLabel(a).localeCompare(getDictionaryItemLabel(b))
      ),
    [inventoryCategoriesQuery.data?.items]
  );

  const filteredInventoryItems = useMemo(() => {
    const filteredItems = inventoryItems
      .filter(
        (item) =>
          item.name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
          item.partNumber?.toLowerCase().includes(searchKeyword.toLowerCase())
      )
      .filter((itemA) =>
        selectedCategoryId ? itemA.category?.id === selectedCategoryId : itemA
      )
      .filter((itemB) =>
        selectedManufacturer
          ? itemB.manufacturer === selectedManufacturer
          : itemB
      )
      .filter((itemC) =>
        selectedInventoryType ? itemC.type === selectedInventoryType : itemC
      )

      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

    return filteredItems;
  }, [
    inventoryItems,
    searchKeyword,
    selectedCategoryId,
    selectedInventoryType,
    selectedManufacturer,
  ]);

  const handleAddItem = useCallback(
    async (itemCount: number) => {
      if (typeof chosenItem?.id !== "string" || typeof task.id !== "string") {
        return;
      }

      if (
        workbookTaskItemToEdit &&
        typeof workbookTaskItemToEdit.id === "string"
      ) {
        await dispatch.failureView.editTaskItemCount({
          inventoryId: chosenItem.id,
          itemCount,
          workbookTaskId: task.id,
          id: workbookTaskItemToEdit.id,
        });
      } else {
        await dispatch.failureView.addTaskItemToWorkbookTask({
          inventoryId: chosenItem.id,
          itemCount,
          workbookTaskId: task.id,
        });
      }

      onCancel?.("" as any);

      if (typeof failure?.id === "string") {
        dispatch.failureView.fetchWorkbook(failure.id);
      }
    },
    [
      chosenItem?.id,
      dispatch.failureView,
      failure?.id,
      onCancel,
      task.id,
      workbookTaskItemToEdit,
    ]
  );

  const chooseItem = useCallback((item: InventoryVm) => {
    setChosenItem(item);

    setContentMode("setCount");
  }, []);

  useEffect(() => {
    if (!visible) {
      setChosenItem(undefined);
      setSearchKeyword("");
      setContentMode("chooseItem");
      resetAllSelect();
    }
  }, [visible]);

  useEffect(() => {
    if (workbookTaskItemToEdit) {
      const taskItemToEdit = inventoryItems.find(
        ({ id }) => workbookTaskItemToEdit.inventoryId === id
      );

      setChosenItem(taskItemToEdit);
      setContentMode("setCount");
    }
  }, [inventoryItems, workbookTaskItemToEdit]);

  const categoryOptions = useMemo(
    () =>
      sortedInventoryCategories
        .filter((item) => item)
        .map((item) => ({
          label: item.name || "",
          value: item.id || "",
        })),
    [sortedInventoryCategories]
  );

  const manufactoryOptions = useMemo(
    () =>
      inventoryItemManufactures
        .filter((item) => item)
        .map((item) => ({
          label: item || "",
          value: item || "",
        })),
    [inventoryItemManufactures]
  );

  const inventoryItemTypeOptions = useMemo(
    () =>
      inventoryItemTypes
        .filter((item) => item)
        .map((item) => ({
          label: item || "",
          value: item || "",
        })),
    [inventoryItemTypes]
  );

  return (
    <ItemChooserModal
      {...rest}
      titleContent={
        <>
          <div className={styles.titleWrapper}>
            <Typography.Title level={2}>
              {contentMode === "chooseItem"
                ? t("workbook.chooseTaskItem")
                : t("workbook.setTaskItemCount")}
            </Typography.Title>
          </div>

          {contentMode === "chooseItem" ? (
            <>
              <ItemChooserSearch
                onChange={(e) => setSearchKeyword(e.target.value)}
                prefix={<MaterialIcon path={mdiMagnify} />}
              />

              <Row justify="center" className={styles.dropdownsWrapper}>
                <Col span={8} className={styles.dropdownCol}>
                  <Typography.Title className={styles.selectLabel} level={5}>
                    {t("failure.itemChooserSearch.category")}
                  </Typography.Title>

                  <Select
                    dropdownClassName={styles.selectDropdown}
                    options={categoryOptions}
                    value={selectedCategoryId}
                    onChange={(id) => handleSelectedCategoryId(id)}
                    listHeight={500}
                    allowClear
                    dropdownMatchSelectWidth={false}
                  />
                </Col>

                <Col span={8} className={styles.dropdownCol}>
                  <Typography.Title className={styles.selectLabel} level={5}>
                    {t("failure.itemChooserSearch.manufacturer")}
                  </Typography.Title>

                  <Select
                    dropdownClassName={styles.selectDropdown}
                    options={manufactoryOptions}
                    value={selectedManufacturer}
                    onChange={(id) => handleSelectedManufacturer(id)}
                    allowClear
                    listHeight={500}
                    dropdownMatchSelectWidth={false}
                  />
                </Col>

                <Col span={8} className={styles.dropdownCol}>
                  <Typography.Title className={styles.selectLabel} level={5}>
                    {t("failure.itemChooserSearch.inventoryType")}
                  </Typography.Title>

                  <Select
                    dropdownClassName={styles.selectDropdown}
                    options={inventoryItemTypeOptions}
                    value={selectedInventoryType}
                    onChange={(id) => handleSelectedInventoryType(id)}
                    listHeight={500}
                    allowClear
                    dropdownMatchSelectWidth={false}
                  />
                </Col>
              </Row>
            </>
          ) : null}
        </>
      }
      onCancel={onCancel}
      visible={visible}
      className={clsx(styles.itemChooserModalWrapper, {
        [styles.desktop]: deviceLayoutType === "default",
      })}
      longModal
    >
      {contentMode === "chooseItem" && (
        <Spin spinning={fetchInventoryItems.loading}>
          <Row gutter={[22, 22]}>
            {filteredInventoryItems.length ? (
              filteredInventoryItems.map((item) => (
                <Col
                  span={8}
                  key={item.id}
                  className={styles.itemChooserColumn}
                >
                  <ItemChooserTask onClick={() => chooseItem(item)}>
                    <div className={styles.itemData}>
                      <div>
                        <div className={styles.itemManufacturer}>
                          {item.manufacturer}
                        </div>

                        <div className={styles.itemNameWrapper}>
                          <p className={styles.itemName}>{item.name}</p>
                        </div>
                      </div>

                      {!!item.image?.imageThumbnails?.length && (
                        <ImageDisplay
                          data={item.image?.imageThumbnails[0]}
                          className={styles.itemImage}
                        />
                      )}
                    </div>
                  </ItemChooserTask>
                </Col>
              ))
            ) : (
              <Col span={24}>
                <ConfigProvider
                  locale={{
                    locale: "hu",
                    Empty: {
                      description: t("common.noDataForSearchValue"),
                    },
                  }}
                >
                  <Empty />
                </ConfigProvider>
              </Col>
            )}
          </Row>
        </Spin>
      )}

      {contentMode === "setCount" && (
        <SetItemCount
          item={chosenItem}
          onAddItem={handleAddItem}
          addButtonIsLoading={
            addTaskItemToWorkbookTask.loading || editTaskItemCount.loading
          }
          defaultItemCount={workbookTaskItemToEdit?.itemCount}
          isSealed={failure?.status?.isSealed}
        />
      )}
    </ItemChooserModal>
  );
};

export default AddTaskItemModal;
